import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/organisms/PageLayout';
import api from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { track } from '../../utils/analytics';
import '../../styles/ai-tools-page.css';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'AI Tools' }
];

interface Job {
  uid: string;
  job_title: string;
  company_name: string;
  kanban_uid: string;
}

interface AIToolCardProps {
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
  disabled?: boolean;
}

const AIToolCard: React.FC<AIToolCardProps> = ({ title, description, actionLabel, onAction, disabled }) => {
  return (
    <div className={`ai-tool-card ${disabled ? 'ai-tool-card--disabled' : ''}`}>
      <h3 className="ai-tool-card__title">{title}</h3>
      <p className="ai-tool-card__description">{description}</p>
      <button 
        className="ai-tool-card__action" 
        onClick={onAction}
        disabled={disabled}
      >
        {actionLabel} <span>→</span>
      </button>
    </div>
  );
};

interface AIToolSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const AIToolSection: React.FC<AIToolSectionProps> = ({ title, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="ai-tools-section">
      <button 
        className="ai-tools-section__header"
        onClick={() => setExpanded(!expanded)}
      >
        <span className={`ai-tools-section__icon ${expanded ? 'ai-tools-section__icon--expanded' : ''}`}>
          ⌃
        </span>
        <h2 className="ai-tools-section__title">{title}</h2>
      </button>
      {expanded && (
        <div className="ai-tools-section__content">
          {children}
        </div>
      )}
    </div>
  );
};

const AITools: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'OfferFlow | AI Tools';
    track('ai_tools_viewed');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchJobs = async () => {
      try {
        const jobsData = await api.get<Job[]>(API_ENDPOINTS.jobs.myJobs);
        if (cancelled) return;
        if (Array.isArray(jobsData)) {
          setJobs(jobsData);
        }
      } catch (error) {
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchJobs();
    return () => { cancelled = true; };
  }, []);

  const selectedJobData = jobs.find(j => j.uid === selectedJob);
  const isJobSelected = !!selectedJob;

  const handleTryAITools = () => {
    if (selectedJob && selectedJobData) {
      track('ai_tool_started', { job_title: selectedJobData.job_title, company: selectedJobData.company_name });
      navigate(`/track/board/${selectedJobData.kanban_uid}/job/${selectedJob}?tab=ai-tools`);
    }
  };

  const handleCardAction = (toolTitle: string, taskType: string) => {
    if (!selectedJob || !selectedJobData) return;
    track('ai_tool_card_clicked', {
      tool: toolTitle,
      job_title: selectedJobData.job_title,
      company: selectedJobData.company_name,
    });
    const encodedTool = encodeURIComponent(taskType);
    navigate(
      `/track/board/${selectedJobData.kanban_uid}/job/${selectedJob}?tab=ai-tools&tool=${encodedTool}`,
    );
  };

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="ai-tools-page">
        <div className="ai-tools-page__left">
          <div className="ai-tools-page__intro">
            <span className="ai-tools-page__label">AI Tools</span>
            <h1 className="ai-tools-page__title">
              Cover Letters and More, With the Help of AI
            </h1>
            <p className="ai-tools-page__subtitle">
              Build cover letters, interview prep questions, offer acceptance messages, and more with our AI tools. Select a job below to get started.
            </p>
          </div>

          <div className="ai-tools-page__selector">
            <div className="ai-tools-page__selector-header">
              <label className="ai-tools-page__selector-label">Target Job</label>
              <span className="ai-tools-page__selector-required">Required</span>
            </div>
            <div className="ai-tools-page__dropdown-wrapper">
              <button 
                className="ai-tools-page__dropdown-btn"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className={selectedJob ? '' : 'ai-tools-page__dropdown-placeholder'}>
                  {selectedJobData 
                    ? `${selectedJobData.job_title} | ${selectedJobData.company_name}`
                    : 'Select one of your saved jobs'
                  }
                </span>
                <span className="ai-tools-page__dropdown-arrow">▾</span>
              </button>
              {isDropdownOpen && (
                <div className="ai-tools-page__dropdown-menu">
                  {loading ? (
                    <div className="ai-tools-page__dropdown-item ai-tools-page__dropdown-item--disabled">
                      Loading jobs...
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="ai-tools-page__dropdown-item ai-tools-page__dropdown-item--disabled">
                      No jobs found. Add jobs to your tracker first.
                    </div>
                  ) : (
                    jobs.map((job) => (
                      <button
                        key={job.uid}
                        className={`ai-tools-page__dropdown-item ${selectedJob === job.uid ? 'ai-tools-page__dropdown-item--selected' : ''}`}
                        onClick={() => {
                          setSelectedJob(job.uid);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {job.job_title} | {job.company_name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button 
              className="ai-tools-page__try-btn"
              disabled={!isJobSelected}
              onClick={handleTryAITools}
            >
              Try AI Tools
            </button>
          </div>
        </div>

        <div className="ai-tools-page__right">
          <AIToolSection title="Application Stage">
            <div className="ai-tools-section__grid ai-tools-section__grid--2">
              <AIToolCard
                title="Cover Letter"
                description="A personalized letter to employers, explaining why you are good fit for the position and how your skills and experiences align with the company's needs."
                actionLabel="Generate Cover Letter"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Cover Letter', 'cover letter')}
              />
              <AIToolCard
                title="Follow Up After Application"
                description="If there's no response from the employer within a reasonable timeframe, a polite follow-up email can be sent to inquire about the status of the application."
                actionLabel="Generate Follow Up"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Follow Up After Application', 'follow up after application')}
              />
            </div>
          </AIToolSection>

          <AIToolSection title="Interview Stage">
            <div className="ai-tools-section__grid ai-tools-section__grid--3">
              <AIToolCard
                title="Interview Prep Questions"
                description="A list of questions to help you prepare for an upcoming interview."
                actionLabel="Generate Interview Prep"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Interview Prep Questions', 'interview prep questions')}
              />
              <AIToolCard
                title="Questions to Ask"
                description="Generate a curated list of questions for job interviewees to ask employers, aimed at gaining deeper insights into the role and company culture."
                actionLabel="Generate Questions"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Questions to Ask', 'questions to ask')}
              />
              <AIToolCard
                title="Thank You After Interview"
                description="Sent shortly after an interview, this message expresses gratitude for the interview, reinforces your interest in the position, and briefly restates why you're a good fit."
                actionLabel="Generate Thank You"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Thank You After Interview', 'thank you after interview')}
              />
              <AIToolCard
                title="Follow Up After Interview"
                description="If there hasn't been a response within the expected timeframe, this email is sent to inquire about the status of the application and to reiterate interest in the position."
                actionLabel="Generate Follow Up"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Follow Up After Interview', 'follow up after interview')}
              />
              <AIToolCard
                title="Reschedule Interview"
                description="In case of unforeseen circumstances that require rescheduling an interview, this email is used to request a new date and time."
                actionLabel="Generate Reschedule"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Reschedule Interview', 'reschedule interview')}
              />
              <AIToolCard
                title="Decline Interview"
                description="An email to decline an interview, expressing your apologies and providing a brief explanation for your decision."
                actionLabel="Generate Decline"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Decline Interview', 'decline interview')}
              />
            </div>
          </AIToolSection>

          <AIToolSection title="Offer Stage">
            <div className="ai-tools-section__grid ai-tools-section__grid--3">
              <AIToolCard
                title="Offer Negotiation"
                description="An email to negotiate the terms of a job offer, including salary, benefits, and other terms."
                actionLabel="Generate Negotiation"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Offer Negotiation', 'offer negotiation')}
              />
              <AIToolCard
                title="Offer Acceptance"
                description="A message confirming the acceptance of the job offer. It usually includes gratitude for the opportunity, confirmation of the terms, and enthusiasm."
                actionLabel="Generate Acceptance"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Offer Acceptance', 'offer acceptance')}
              />
              <AIToolCard
                title="Offer Decline"
                description="An email to decline a job offer, expressing your gratitude and providing a brief explanation for your decision."
                actionLabel="Generate Decline"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Offer Decline', 'offer decline')}
              />
              <AIToolCard
                title="Offer Time Extension"
                description="A message asking for more time to consider a job offer. It typically includes valid reasons for the request and expresses continued interest in the position."
                actionLabel="Generate Extension"
                disabled={!isJobSelected}
                onAction={() => handleCardAction('Offer Time Extension', 'offer time extensions request')}
              />
            </div>
          </AIToolSection>
        </div>
      </div>
    </PageLayout>
  );
};

export default AITools;
